import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, ShoppingBag, CalendarDays, Clock, DollarSign, Users, Ticket, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { Logo } from "@/components/Logo";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  pending: "text-cosmic-yellow border-cosmic-yellow/40 bg-cosmic-yellow/10",
  confirmed: "text-cosmic-cyan border-cosmic-cyan/40 bg-cosmic-cyan/10",
  completed: "text-cosmic-green border-cosmic-green/40 bg-cosmic-green/10",
  cancelled: "text-slate-400 border-white/15 bg-white/5",
};

const StatusSelect = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wide bg-transparent cursor-pointer focus:outline-none ${STATUS_COLORS[value] || STATUS_COLORS.pending}`}>
    {STATUSES.map((s) => <option key={s} value={s} className="bg-cosmic-elevated text-white">{s}</option>)}
  </select>
);

const nextReward = (containers) => {
  const mod = containers % 10;
  if (mod < 5) return `${5 - mod} to half-off · ${10 - mod} to free`;
  return `${10 - mod} to a free one`;
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [members, setMembers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: "", kind: "percent", value: 10, scope: "all", one_per_email: false });
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  const authed = useCallback(async (fn) => {
    try {
      return await fn();
    } catch (e) {
      if (e?.response?.status === 401) {
        try {
          await api.post("/auth/refresh");
          return await fn();
        } catch {
          setUser(false);
          throw e;
        }
      }
      throw e;
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [o, b, m, c, s] = await authed(() => Promise.all([
        api.get("/admin/orders"), api.get("/admin/bookings"), api.get("/admin/members"),
        api.get("/admin/coupons"), api.get("/admin/stats"),
      ]));
      setOrders(o.data);
      setBookings(b.data);
      setMembers(m.data);
      setCoupons(c.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Failed to load dashboard data");
    }
  }, [authed]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setLoginError(formatApiError(err));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const updateStatus = async (kind, id, status) => {
    try {
      await authed(() => api.patch(`/admin/${kind}/${id}`, { status }));
      if (kind === "orders") setOrders((list) => list.map((o) => (o.order_id === id ? { ...o, status } : o)));
      else setBookings((list) => list.map((b) => (b.booking_id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    if (creatingCoupon) return;
    setCreatingCoupon(true);
    try {
      const { data } = await authed(() => api.post("/admin/coupons", { ...couponForm, value: Number(couponForm.value) }));
      setCoupons((c) => [data, ...c]);
      setCouponForm({ code: "", kind: "percent", value: 10, scope: "all", one_per_email: false });
      toast.success(`Code ${data.code} created`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingCoupon(false);
    }
  };

  const toggleCoupon = async (code, active) => {
    try {
      await authed(() => api.patch(`/admin/coupons/${code}`, { active }));
      setCoupons((c) => c.map((x) => (x.code === code ? { ...x, active } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center bg-cosmic-base text-slate-400" data-testid="admin-loading">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-cosmic-base bg-nebula px-4" data-testid="admin-login-page">
        <div className="grain-overlay" />
        <motion.form
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          onSubmit={login}
          className="glass w-full max-w-sm rounded-3xl p-8"
          data-testid="admin-login-form">
          <div className="flex flex-col items-center">
            <Logo size={52} />
            <h1 className="font-display mt-4 text-2xl font-black text-white">Owner Console</h1>
            <p className="mt-1 text-xs text-slate-400">Cosmic Cloudz mission control</p>
          </div>
          <input data-testid="admin-email-input" type="email" required placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-7 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
          <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
          {loginError && <p className="mt-3 text-xs text-cosmic-pink" data-testid="admin-login-error">{loginError}</p>}
          <button data-testid="admin-login-button" type="submit" disabled={loggingIn}
            className="mt-5 w-full rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple py-3.5 font-display text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.02] glow-pink disabled:opacity-50">
            {loggingIn ? "Signing in…" : "Sign In"}
          </button>
          <Link to="/" className="mt-5 block text-center text-xs text-slate-500 hover:text-cosmic-cyan" data-testid="admin-back-home">
            Back to site
          </Link>
        </motion.form>
      </div>
    );
  }

  const TABS = [
    ["orders", `Orders (${orders.length})`],
    ["bookings", `Bookings (${bookings.length})`],
    ["members", `Members (${members.length})`],
    ["coupons", `Coupons (${coupons.length})`],
  ];

  return (
    <div className="min-h-screen bg-cosmic-base text-white" data-testid="admin-dashboard">
      <div className="grain-overlay" />
      <header className="sticky top-0 z-30 glass border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <span className="font-display text-lg font-black">Mission <span className="text-gradient-candy">Control</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-button" onClick={loadData} aria-label="Refresh"
              className="rounded-full glass p-2.5 text-slate-300 hover:text-cosmic-cyan"><RefreshCw size={16} /></button>
            <Link to="/" data-testid="admin-view-site" className="hidden sm:block rounded-full glass px-4 py-2 text-xs font-bold text-slate-300 hover:text-white">
              View Site
            </Link>
            <button data-testid="admin-logout-button" onClick={logout}
              className="flex items-center gap-1.5 rounded-full glass px-4 py-2 text-xs font-bold text-slate-300 hover:text-cosmic-pink">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {stats && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
            {[
              { label: "Orders", value: stats.orders, icon: ShoppingBag, color: "text-cosmic-cyan" },
              { label: "Bookings", value: stats.bookings, icon: CalendarDays, color: "text-cosmic-pink" },
              { label: "Pending", value: stats.pending, icon: Clock, color: "text-cosmic-yellow" },
              { label: "Members", value: stats.members, icon: Users, color: "text-cosmic-purple" },
              { label: "Est. Revenue", value: `$${stats.revenue.toFixed(2)}`, icon: DollarSign, color: "text-cosmic-green" },
            ].map((s) => (
              <div key={s.label} className="glass rounded-2xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <s.icon size={18} className={s.color} />
                <div className="font-display mt-3 text-3xl font-black">{s.value}</div>
                <div className="font-mono mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-400">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-9 flex flex-wrap gap-1.5 rounded-2xl sm:rounded-full glass p-1.5 sm:inline-flex" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${tab === key ? "bg-gradient-to-r from-cosmic-pink to-cosmic-purple text-white" : "text-slate-300 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {tab === "orders" && (
            orders.length === 0 ? (
              <p className="glass rounded-2xl p-10 text-center text-sm text-slate-400" data-testid="orders-empty">No orders yet. Share the site and watch them roll in.</p>
            ) : orders.map((o) => (
              <div key={o.order_id} className="glass rounded-2xl p-5" data-testid={`admin-order-${o.order_id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-cosmic-cyan">#{o.order_id.slice(0, 8).toUpperCase()}</span>
                      <span className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1.5 font-display text-lg font-bold">{o.customer_name}</div>
                    <div className="text-xs text-slate-400">{o.email} · {o.phone} · {o.fulfillment} · pays via {o.payment_method}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex flex-col items-end gap-1">
                      <span className="font-display text-xl font-black text-gradient-candy">${o.total.toFixed(2)}</span>
                      {o.payment_status === "paid" ? (
                        <span data-testid={`order-paid-${o.order_id.slice(0, 8)}`} className="rounded-full border border-cosmic-green/40 bg-cosmic-green/10 px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-cosmic-green">Paid by card</span>
                      ) : (
                        <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Unpaid</span>
                      )}
                    </span>
                    <StatusSelect value={o.status} testid={`order-status-${o.order_id.slice(0, 8)}`}
                      onChange={(s) => updateStatus("orders", o.order_id, s)} />
                  </div>
                </div>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {o.items.map((i) => (
                    <li key={`${i.flavor_id}-${i.size}`} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
                      {i.name} ({i.size}) × {i.qty}
                    </li>
                  ))}
                  {o.loyalty_discount > 0 && (
                    <li className="rounded-full border border-cosmic-green/30 bg-cosmic-green/10 px-3 py-1 text-xs text-cosmic-green">
                      Club reward −${o.loyalty_discount.toFixed(2)}
                    </li>
                  )}
                  {o.coupon_code && (
                    <li className="rounded-full border border-cosmic-yellow/30 bg-cosmic-yellow/10 px-3 py-1 text-xs text-cosmic-yellow">
                      {o.coupon_code} −${(o.coupon_discount || 0).toFixed(2)}
                    </li>
                  )}
                </ul>
                {o.notes && <p className="mt-3 text-xs italic text-slate-400">“{o.notes}”</p>}
              </div>
            ))
          )}

          {tab === "bookings" && (
            bookings.length === 0 ? (
              <p className="glass rounded-2xl p-10 text-center text-sm text-slate-400" data-testid="bookings-empty">No booking requests yet.</p>
            ) : bookings.map((b) => (
              <div key={b.booking_id} className="glass rounded-2xl p-5" data-testid={`admin-booking-${b.booking_id.slice(0, 8)}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-cosmic-pink">#{b.booking_id.slice(0, 8).toUpperCase()}</span>
                      <span className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1.5 font-display text-lg font-bold">{b.customer_name} — {b.package_name}</div>
                    <div className="text-xs text-slate-400">
                      {b.email} · {b.phone} · event {b.event_date} · {b.guests} guests · pays via {b.payment_method}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {b.generator ? "Generator (+$100)" : "Venue power"} · {b.miles || 0} mi away
                      {b.travel_fee > 0 && ` · trip charge $${b.travel_fee.toFixed(2)}`}
                      {b.extra_flavors > 0 && ` · ${b.extra_flavors} extra flavor${b.extra_flavors > 1 ? "s" : ""} (+$${(b.extra_flavor_fee || 0).toFixed(0)})`}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex flex-col items-end gap-1">
                      <span className="font-display text-xl font-black text-gradient-candy">${b.total.toFixed(2)}</span>
                      {b.payment_status === "paid" ? (
                        <span data-testid={`booking-paid-${b.booking_id.slice(0, 8)}`} className="rounded-full border border-cosmic-green/40 bg-cosmic-green/10 px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-cosmic-green">Paid by card</span>
                      ) : (
                        <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Unpaid</span>
                      )}
                    </span>
                    <StatusSelect value={b.status} testid={`booking-status-${b.booking_id.slice(0, 8)}`}
                      onChange={(s) => updateStatus("bookings", b.booking_id, s)} />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.flavor_names?.map((f) => (
                    <span key={f} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">{f}</span>
                  ))}
                  {b.bulk_premium?.map((x) => (
                    <span key={x.flavor_id} className="rounded-full border border-cosmic-cyan/30 bg-cosmic-cyan/10 px-3 py-1 text-xs text-cosmic-cyan">
                      Bulk: {x.name} × {x.qty}
                    </span>
                  ))}
                  {b.coupon_code && (
                    <span className="rounded-full border border-cosmic-yellow/30 bg-cosmic-yellow/10 px-3 py-1 text-xs text-cosmic-yellow">
                      {b.coupon_code} −${(b.coupon_discount || 0).toFixed(2)}
                    </span>
                  )}
                </div>
                {b.notes && <p className="mt-3 text-xs italic text-slate-400">“{b.notes}”</p>}
              </div>
            ))
          )}

          {tab === "members" && (
            members.length === 0 ? (
              <p className="glass rounded-2xl p-10 text-center text-sm text-slate-400" data-testid="members-empty">No Cosmic Club members yet.</p>
            ) : members.map((m) => (
              <div key={m.member_id} className="glass rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-member-${m.email}`}>
                <div>
                  <div className="font-display text-lg font-bold">{m.name}</div>
                  <div className="text-xs text-slate-400">{m.email}{m.phone ? ` · ${m.phone}` : ""} · joined {new Date(m.created_at).toLocaleDateString()}</div>
                  <div className="mt-1 font-mono text-[11px] text-cosmic-yellow">Welcome code: {m.welcome_code}</div>
                </div>
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <div className="font-display text-2xl font-black text-cosmic-cyan" data-testid={`member-containers-${m.email}`}>{m.containers}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Containers</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-black text-cosmic-pink">{m.orders}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Orders</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-black text-cosmic-green">${m.spent.toFixed(0)}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Spent</div>
                  </div>
                  <div className="rounded-full border border-cosmic-purple/40 bg-cosmic-purple/10 px-3.5 py-1.5 text-xs font-semibold text-cosmic-purple">
                    {nextReward(m.containers)}
                  </div>
                </div>
              </div>
            ))
          )}

          {tab === "coupons" && (
            <>
              <form onSubmit={createCoupon} className="glass rounded-2xl p-5 flex flex-wrap items-end gap-3" data-testid="coupon-create-form">
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">Code</span>
                  <input data-testid="coupon-code-input" required placeholder="e.g. SUMMER20" value={couponForm.code}
                    onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                    className="w-40 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 font-mono text-sm uppercase tracking-wider text-white placeholder:text-slate-500 focus:border-cosmic-yellow/60 focus:outline-none" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">Type</span>
                  <select data-testid="coupon-kind-select" value={couponForm.kind}
                    onChange={(e) => setCouponForm((f) => ({ ...f, kind: e.target.value }))}
                    className="rounded-xl border border-white/10 bg-cosmic-elevated px-3 py-2.5 text-sm text-white focus:outline-none">
                    <option value="percent">% off</option>
                    <option value="fixed">$ off</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">Value</span>
                  <input data-testid="coupon-value-input" required type="number" min="1" max={couponForm.kind === "percent" ? 100 : 1000} value={couponForm.value}
                    onChange={(e) => setCouponForm((f) => ({ ...f, value: e.target.value }))}
                    className="w-24 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white focus:border-cosmic-yellow/60 focus:outline-none" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">Applies to</span>
                  <select data-testid="coupon-scope-select" value={couponForm.scope}
                    onChange={(e) => setCouponForm((f) => ({ ...f, scope: e.target.value }))}
                    className="rounded-xl border border-white/10 bg-cosmic-elevated px-3 py-2.5 text-sm text-white focus:outline-none">
                    <option value="all">Orders + bookings</option>
                    <option value="orders">Orders only</option>
                    <option value="bookings">Bookings only</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 pb-3 text-xs text-slate-300 cursor-pointer">
                  <input data-testid="coupon-once-checkbox" type="checkbox" checked={couponForm.one_per_email}
                    onChange={(e) => setCouponForm((f) => ({ ...f, one_per_email: e.target.checked }))}
                    className="accent-cosmic-pink" />
                  One use per email
                </label>
                <button data-testid="coupon-create-button" type="submit" disabled={creatingCoupon}
                  className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-cosmic-yellow to-cosmic-pink px-5 py-2.5 text-xs font-bold text-cosmic-base disabled:opacity-50">
                  <Plus size={13} /> Create Code
                </button>
              </form>

              {coupons.length === 0 ? (
                <p className="glass rounded-2xl p-10 text-center text-sm text-slate-400" data-testid="coupons-empty">No codes yet. Create one above and share it.</p>
              ) : coupons.map((c) => (
                <div key={c.code} className="glass rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-coupon-${c.code}`}>
                  <div>
                    <span className="font-mono text-base font-bold tracking-[0.12em] text-cosmic-yellow">{c.code}</span>
                    <div className="mt-1 text-xs text-slate-400">
                      {c.kind === "percent" ? `${c.value}% off` : `$${c.value} off`} · {c.scope === "all" ? "orders + bookings" : c.scope}
                      {c.one_per_email && " · one use per email"}
                      {c.member_only_email && ` · reserved for ${c.member_only_email}`}
                      {c.note ? ` · ${c.note}` : ""}
                    </div>
                  </div>
                  <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggleCoupon(c.code, !c.active)}
                    className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wide ${c.active ? "border-cosmic-green/40 bg-cosmic-green/10 text-cosmic-green" : "border-white/15 bg-white/5 text-slate-400"}`}>
                    {c.active ? "Active" : "Disabled"}
                  </button>
                </div>
              ))
              }
            </>
          )}
        </div>
      </main>
    </div>
  );
}
